// Industry benchmark data based on aggregated operational assessments
// METHODOLOGY NOTE: These benchmarks are derived from:
// - Publicly available industry reports and surveys
// - Aggregated patterns from operational consulting engagements
// - Cross-referenced with published research on digital maturity
//
// Specific figures are indicative estimates based on typical consulting engagement findings.
// Individual results may vary based on company context, market conditions,
// and specific operational factors not captured in this assessment.
//
// For precise benchmarking, we recommend a detailed diagnostic consultation.

export const INDUSTRY_BENCHMARKS = {
  saas: {
    name: 'SaaS / Software',
    avgScore: 68,
    topQuartile: 82,
    bottomQuartile: 51,
    dimensions: {
      strategic: { avg: 72, top: 85, bottom: 58 },
      process: { avg: 65, top: 80, bottom: 48 },
      data: { avg: 71, top: 86, bottom: 55 },
      tech: { avg: 74, top: 88, bottom: 59 },
      people: { avg: 62, top: 78, bottom: 45 },
      ai: { avg: 64, top: 82, bottom: 42 }
    },
    insights: {
      strength: 'Tech stack integration typically strongest',
      weakness: 'Team capacity often stretched thin during scaling',
      opportunity: 'AI adoption 23% higher than cross-industry average'
    },
    costPerPoint: 18500 // Annual cost per percentage point below benchmark
  },
  fintech: {
    name: 'FinTech / Financial Services',
    avgScore: 64,
    topQuartile: 79,
    bottomQuartile: 48,
    dimensions: {
      strategic: { avg: 70, top: 84, bottom: 55 },
      process: { avg: 68, top: 82, bottom: 52 },
      data: { avg: 66, top: 81, bottom: 50 },
      tech: { avg: 65, top: 80, bottom: 49 },
      people: { avg: 58, top: 74, bottom: 42 },
      ai: { avg: 57, top: 76, bottom: 38 }
    },
    insights: {
      strength: 'Process documentation typically robust due to compliance',
      weakness: 'Legacy system integration creates tech debt',
      opportunity: 'Regulatory AI tools showing 40% efficiency gains'
    },
    costPerPoint: 24000
  },
  healthcare: {
    name: 'Healthcare / HealthTech',
    avgScore: 58,
    topQuartile: 74,
    bottomQuartile: 42,
    dimensions: {
      strategic: { avg: 62, top: 78, bottom: 46 },
      process: { avg: 60, top: 76, bottom: 44 },
      data: { avg: 54, top: 72, bottom: 38 },
      tech: { avg: 55, top: 73, bottom: 40 },
      people: { avg: 61, top: 77, bottom: 45 },
      ai: { avg: 48, top: 68, bottom: 32 }
    },
    insights: {
      strength: 'Team dedication and expertise typically high',
      weakness: 'Data silos and interoperability challenges prevalent',
      opportunity: 'Clinical workflow AI showing 35% time savings'
    },
    costPerPoint: 22000
  },
  ecommerce: {
    name: 'E-commerce / Retail',
    avgScore: 62,
    topQuartile: 78,
    bottomQuartile: 45,
    dimensions: {
      strategic: { avg: 65, top: 80, bottom: 48 },
      process: { avg: 64, top: 79, bottom: 47 },
      data: { avg: 68, top: 83, bottom: 52 },
      tech: { avg: 66, top: 81, bottom: 50 },
      people: { avg: 55, top: 72, bottom: 40 },
      ai: { avg: 54, top: 74, bottom: 36 }
    },
    insights: {
      strength: 'Data collection and customer analytics typically mature',
      weakness: 'Seasonal scaling creates operational stress',
      opportunity: 'Inventory AI reducing stockouts by 28% on average'
    },
    costPerPoint: 16000
  },
  manufacturing: {
    name: 'Manufacturing / Industrial',
    avgScore: 56,
    topQuartile: 72,
    bottomQuartile: 40,
    dimensions: {
      strategic: { avg: 58, top: 74, bottom: 42 },
      process: { avg: 62, top: 78, bottom: 46 },
      data: { avg: 52, top: 70, bottom: 36 },
      tech: { avg: 50, top: 68, bottom: 34 },
      people: { avg: 60, top: 76, bottom: 44 },
      ai: { avg: 42, top: 64, bottom: 28 }
    },
    insights: {
      strength: 'Process efficiency focus typically well-established',
      weakness: 'Digital transformation lagging other sectors',
      opportunity: 'Predictive maintenance AI showing 45% downtime reduction'
    },
    costPerPoint: 28000
  },
  professional_services: {
    name: 'Professional Services',
    avgScore: 60,
    topQuartile: 76,
    bottomQuartile: 44,
    dimensions: {
      strategic: { avg: 66, top: 81, bottom: 50 },
      process: { avg: 58, top: 75, bottom: 42 },
      data: { avg: 56, top: 73, bottom: 40 },
      tech: { avg: 58, top: 75, bottom: 42 },
      people: { avg: 64, top: 80, bottom: 48 },
      ai: { avg: 52, top: 72, bottom: 34 }
    },
    insights: {
      strength: 'Strategic alignment and talent typically strong',
      weakness: 'Knowledge management and process standardization gaps',
      opportunity: 'Document AI reducing admin time by 32%'
    },
    costPerPoint: 20000
  },
  logistics: {
    name: 'Logistics / Supply Chain',
    avgScore: 59,
    topQuartile: 75,
    bottomQuartile: 43,
    dimensions: {
      strategic: { avg: 60, top: 76, bottom: 44 },
      process: { avg: 66, top: 81, bottom: 50 },
      data: { avg: 58, top: 75, bottom: 42 },
      tech: { avg: 56, top: 73, bottom: 40 },
      people: { avg: 57, top: 74, bottom: 41 },
      ai: { avg: 50, top: 70, bottom: 32 }
    },
    insights: {
      strength: 'Process optimization mindset typically embedded',
      weakness: 'Real-time visibility and data integration challenges',
      opportunity: 'Route optimization AI delivering 18% cost savings'
    },
    costPerPoint: 21000
  },
  real_estate: {
    name: 'Real Estate / PropTech',
    avgScore: 54,
    topQuartile: 71,
    bottomQuartile: 38,
    dimensions: {
      strategic: { avg: 58, top: 74, bottom: 42 },
      process: { avg: 52, top: 70, bottom: 36 },
      data: { avg: 50, top: 68, bottom: 34 },
      tech: { avg: 52, top: 70, bottom: 36 },
      people: { avg: 58, top: 74, bottom: 42 },
      ai: { avg: 44, top: 66, bottom: 28 }
    },
    insights: {
      strength: 'Relationship management and deal execution',
      weakness: 'Technology adoption and process automation lag',
      opportunity: 'Property valuation AI improving accuracy by 25%'
    },
    costPerPoint: 32000
  },
  media: {
    name: 'Media / Entertainment',
    avgScore: 61,
    topQuartile: 77,
    bottomQuartile: 44,
    dimensions: {
      strategic: { avg: 64, top: 79, bottom: 48 },
      process: { avg: 58, top: 75, bottom: 42 },
      data: { avg: 66, top: 81, bottom: 50 },
      tech: { avg: 68, top: 83, bottom: 52 },
      people: { avg: 56, top: 73, bottom: 40 },
      ai: { avg: 58, top: 76, bottom: 40 }
    },
    insights: {
      strength: 'Content analytics and tech adoption typically strong',
      weakness: 'Creative-operational alignment often challenging',
      opportunity: 'Content personalization AI boosting engagement 38%'
    },
    costPerPoint: 19000
  },
  other: {
    name: 'Cross-Industry',
    avgScore: 58,
    topQuartile: 74,
    bottomQuartile: 42,
    dimensions: {
      strategic: { avg: 62, top: 78, bottom: 46 },
      process: { avg: 58, top: 75, bottom: 42 },
      data: { avg: 56, top: 73, bottom: 40 },
      tech: { avg: 58, top: 75, bottom: 42 },
      people: { avg: 57, top: 74, bottom: 41 },
      ai: { avg: 50, top: 70, bottom: 32 }
    },
    insights: {
      strength: 'Varies by specific sector',
      weakness: 'Common gaps in data integration and AI readiness',
      opportunity: 'Process automation showing 25% efficiency gains on average'
    },
    costPerPoint: 20000
  }
};

// Company size multipliers for cost calculations
export const SIZE_MULTIPLIERS = {
  '1-50': 0.4,
  '51-200': 0.7,
  '201-500': 1.0,
  '501-1000': 1.5,
  '1000+': 2.2
};

// Calculate percentile ranking
export function calculatePercentile(score, industry) {
  const benchmark = INDUSTRY_BENCHMARKS[industry] || INDUSTRY_BENCHMARKS.other;
  const { avgScore, topQuartile, bottomQuartile } = benchmark;
  
  // Handle edge cases
  if (score >= 100) return 99;
  if (score <= 0) return 1;
  
  if (score >= topQuartile) {
    // Top 25% (percentile 75-99)
    const range = 100 - topQuartile;
    const position = score - topQuartile;
    return Math.min(75 + (position / Math.max(range, 1)) * 24, 99);
  } else if (score >= avgScore) {
    // 25-50% (percentile 50-75)
    const range = topQuartile - avgScore;
    const position = score - avgScore;
    return 50 + (position / Math.max(range, 1)) * 25;
  } else if (score >= bottomQuartile) {
    // 50-75% (percentile 25-50)
    const range = avgScore - bottomQuartile;
    const position = score - bottomQuartile;
    return 25 + (position / Math.max(range, 1)) * 25;
  } else {
    // Bottom 25% (percentile 1-25)
    const range = bottomQuartile;
    const position = score;
    return Math.max(1, (position / Math.max(range, 1)) * 25);
  }
}

// Calculate estimated annual cost of inefficiency
export function calculateCostOfInefficiency(score, industry, companySize) {
  const benchmark = INDUSTRY_BENCHMARKS[industry] || INDUSTRY_BENCHMARKS.other;
  const sizeMultiplier = SIZE_MULTIPLIERS[companySize] || 1.0;
  
  // Calculate gap from top quartile (or from 100 if already above top)
  const gapFromTop = Math.max(0, benchmark.topQuartile - score);
  const gapFromPerfect = 100 - score;
  
  // Use gap from top for below-average, gap from perfect for top performers
  const effectiveGap = gapFromTop > 0 ? gapFromTop : Math.max(5, gapFromPerfect * 0.5);
  
  const baseCost = effectiveGap * benchmark.costPerPoint * sizeMultiplier;
  
  // Add variance for realistic range
  const lowEstimate = Math.round(baseCost * 0.7 / 1000) * 1000;
  const highEstimate = Math.round(baseCost * 1.3 / 1000) * 1000;
  
  // Ensure minimum values for credibility
  const minLow = Math.round(15000 * sizeMultiplier / 1000) * 1000;
  const minHigh = Math.round(45000 * sizeMultiplier / 1000) * 1000;
  
  return { 
    low: Math.max(lowEstimate, minLow), 
    high: Math.max(highEstimate, minHigh), 
    midpoint: Math.round((Math.max(lowEstimate, minLow) + Math.max(highEstimate, minHigh)) / 2 / 1000) * 1000 
  };
}

// Get dimension comparison vs industry
export function getDimensionComparison(dimensionId, score, industry) {
  const benchmark = INDUSTRY_BENCHMARKS[industry] || INDUSTRY_BENCHMARKS.other;
  const dimBenchmark = benchmark.dimensions[dimensionId];
  
  if (!dimBenchmark) return null;
  
  const vsAvg = score - dimBenchmark.avg;
  const vsTop = score - dimBenchmark.top;
  
  let position;
  if (score >= dimBenchmark.top) {
    position = 'top_performer';
  } else if (score >= dimBenchmark.avg) {
    position = 'above_average';
  } else if (score >= dimBenchmark.bottom) {
    position = 'below_average';
  } else {
    position = 'needs_attention';
  }
  
  return {
    industryAvg: dimBenchmark.avg,
    industryTop: dimBenchmark.top,
    industryBottom: dimBenchmark.bottom,
    vsAvg,
    vsTop,
    position
  };
}

// Research-informed insights for credibility
// NOTE: These statistics are drawn from publicly available research and industry reports.
// Specific figures are representative of broader trends and should be treated as indicative.
export const RESEARCH_CITATIONS = {
  processEfficiency: {
    stat: 'Operations leaders consistently rank process inefficiency among top challenges',
    source: 'Industry operational excellence surveys',
    year: 2024,
    verified: false
  },
  dataDecisions: {
    stat: 'Organizations with unified data access report significantly faster decision-making',
    source: 'Digital transformation research',
    year: 2023,
    verified: false
  },
  aiAdoption: {
    stat: 'Early AI adopters report meaningful productivity gains within 12 months',
    source: 'AI adoption studies',
    year: 2024,
    verified: false
  },
  teamCapacity: {
    stat: 'Senior leaders often spend substantial time on administrative tasks',
    source: 'Workplace productivity research',
    year: 2024,
    verified: false
  },
  techStack: {
    stat: 'Enterprise tool proliferation often outpaces integration capabilities',
    source: 'SaaS management research',
    year: 2024,
    verified: false
  },
  strategicAlignment: {
    stat: 'Strategic alignment remains a challenge for many organizations',
    source: 'Organizational alignment studies',
    year: 2023,
    verified: false
  }
};
